import React, { useEffect, useMemo, useState, useRef } from "react";
import { MapPin, ChevronRight } from "lucide-react";
import {
  catalogApi,
  channelPartnerApi,
  toApiAbsoluteUrl,
} from "../../api/client";
import {
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  LinearProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { useLocation, useNavigate, Link } from "react-router-dom";

export default function ChannelPartnerListings() {
  const [listings, setListings] = useState([]);
  const [listingsTotal, setListingsTotal] = useState(0);
  const [filters, setFilters] = useState({
    q: "",
    categoryIds: [],
    subcategoryIds: [],
    tags: "",
    priceMin: "",
    priceMax: "",
    lat: "",
    lng: "",
    sort: "created_desc",
    page: 1,
    limit: 20,
    providerUserId: "",
  });
  const [subcategories, setSubcategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [providers, setProviders] = useState([]);
  const [channelPartnerProviderIds, setChannelPartnerProviderIds] = useState(
    []
  );
  const [firstSearchDone, setFirstSearchDone] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isInitializedRef = useRef(false);

  const urlProviderId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("providerUserId") || "";
  }, [location.search]);

  const currencyFmt = useMemo(
    () =>
      new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    []
  );

  useEffect(() => {
    (async () => {
      try {
        const cat = await catalogApi.listCategories();
        setCategories(cat?.items || []);
        try {
          const res = await channelPartnerApi.listProviders({
            page: 1,
            limit: 500,
          });
          const opts = (res?.providers || []).map((p) => ({
            id: p.id,
            label: p?.companyName || p.email,
          }));
          setProviders(opts);
          // Store provider IDs for filtering listings
          setChannelPartnerProviderIds(opts.map((p) => p.id));
        } catch {
          setProviders([]);
          setChannelPartnerProviderIds([]);
        }
      } catch {
        setCategories([]);
      }
    })();
  }, []);

  // Trigger initial search when providers are loaded and no URL provider ID
  useEffect(() => {
    if (
      channelPartnerProviderIds.length > 0 &&
      !urlProviderId &&
      !isInitializedRef.current
    ) {
      runSearch({ page: 1 }).finally(() => {
        isInitializedRef.current = true;
        setFirstSearchDone(true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelPartnerProviderIds, urlProviderId]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((p) => ({ ...p, q: searchText, page: 1 }));
    }, 200);
    return () => clearTimeout(handle);
  }, [searchText]);

  useEffect(() => {
    if (urlProviderId) {
      setFilters((p) => ({ ...p, providerUserId: urlProviderId, page: 1 }));
      runSearch({ page: 1, providerUserId: urlProviderId }).finally(() => {
        isInitializedRef.current = true;
        setFirstSearchDone(true);
      });
    } else {
      // Wait for providers to be loaded before running initial search
      if (channelPartnerProviderIds.length > 0) {
        runSearch({ page: 1 }).finally(() => {
          isInitializedRef.current = true;
          setFirstSearchDone(true);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlProviderId, channelPartnerProviderIds]);

  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (urlProviderId && filters.providerUserId !== urlProviderId) return;
    runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.q,
    filters.categoryIds,
    filters.subcategoryIds,
    filters.tags,
    filters.priceMin,
    filters.priceMax,
    filters.sort,
    filters.page,
    filters.limit,
    filters.providerUserId,
  ]);

  const handleCategoriesChange = async (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;
    setFilters((p) => ({ ...p, categoryIds: selected, subcategoryIds: [] }));
    if (selected.length > 0) {
      try {
        const all = await Promise.all(
          selected.map((id) => catalogApi.listSubcategories(id))
        );
        const merged = (all || []).flatMap((r) => r?.items || []);
        const seen = new Set();
        const unique = [];
        for (const s of merged) {
          if (!seen.has(s._id)) {
            seen.add(s._id);
            unique.push(s);
          }
        }
        setSubcategories(unique);
      } catch {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  };

  const handleSubcategoriesChange = (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;
    setFilters((p) => ({ ...p, subcategoryIds: selected }));
  };

  async function runSearch(override = {}) {
    const merged = { ...filters, ...override };

    try {
      setLoading(true);

      // If a specific provider is selected, use normal search
      if (merged.providerUserId) {
        const params = {};
        if (merged.q) params.q = merged.q;
        if (Array.isArray(merged.categoryIds) && merged.categoryIds.length > 0)
          params.categoryIds = merged.categoryIds.join(",");
        if (
          Array.isArray(merged.subcategoryIds) &&
          merged.subcategoryIds.length > 0
        )
          params.subcategoryIds = merged.subcategoryIds.join(",");
        if (merged.tags) params.tags = merged.tags;
        if (merged.priceMin !== "" && merged.priceMin !== null)
          params.priceMin = Number(merged.priceMin);
        if (merged.priceMax !== "" && merged.priceMax !== null)
          params.priceMax = Number(merged.priceMax);
        if (merged.lat !== "" && merged.lng !== "") {
          params.lat = Number(merged.lat);
          params.lng = Number(merged.lng);
        }
        if (merged.page) params.page = Number(merged.page);
        if (merged.limit) params.limit = Number(merged.limit);
        if (merged.sort) params.sort = merged.sort;
        params.providerUserId = merged.providerUserId;

        const res = await catalogApi.publicSearch(params);
        setListings(res?.items || []);
        setListingsTotal(res?.total || 0);
      } else {
        // If no specific provider selected, search across all channel partner's providers
        if (channelPartnerProviderIds.length === 0) {
          setListings([]);
          setListingsTotal(0);
          setFilters((prev) => ({ ...prev, ...override }));
          return;
        }

        // Build base params without providerUserId
        const baseParams = {};
        if (merged.q) baseParams.q = merged.q;
        if (Array.isArray(merged.categoryIds) && merged.categoryIds.length > 0)
          baseParams.categoryIds = merged.categoryIds.join(",");
        if (
          Array.isArray(merged.subcategoryIds) &&
          merged.subcategoryIds.length > 0
        )
          baseParams.subcategoryIds = merged.subcategoryIds.join(",");
        if (merged.tags) baseParams.tags = merged.tags;
        if (merged.priceMin !== "" && merged.priceMin !== null)
          baseParams.priceMin = Number(merged.priceMin);
        if (merged.priceMax !== "" && merged.priceMax !== null)
          baseParams.priceMax = Number(merged.priceMax);
        if (merged.lat !== "" && merged.lng !== "") {
          baseParams.lat = Number(merged.lat);
          baseParams.lng = Number(merged.lng);
        }
        if (merged.sort) baseParams.sort = merged.sort;

        // Search each provider and combine results
        const allListings = [];

        for (const providerId of channelPartnerProviderIds) {
          const params = { ...baseParams, providerUserId: providerId };
          const res = await catalogApi.publicSearch(params);
          if (res?.items) {
            allListings.push(...res.items);
          }
        }

        // Sort combined results
        if (merged.sort === "price_asc") {
          allListings.sort((a, b) => (a.price || 0) - (b.price || 0));
        } else if (merged.sort === "price_desc") {
          allListings.sort((a, b) => (b.price || 0) - (a.price || 0));
        } else {
          allListings.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
        }

        // Apply pagination to combined results
        const page = merged.page || 1;
        const limit = merged.limit || 20;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedListings = allListings.slice(startIndex, endIndex);

        setListings(paginatedListings);
        setListingsTotal(allListings.length);
      }

      setFilters((prev) => ({ ...prev, ...override }));
    } catch (_e) {
      setListings([]);
      setListingsTotal(0);
      const msg = _e?.data?.message || "Failed to load listings";
      setSnackbar({ open: true, message: msg });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <div className="mb-4 text-sm text-gray-600 flex items-center gap-1">
        <Link to="/channel-partner/dashboard" className="hover:text-gray-800">
          Dashboard
        </Link>
        <ChevronRight className="h-4 w-4 opacity-60" />
        <span className="text-gray-800 font-medium">My Listings</span>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded p-4 mb-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <input
          type="text"
          placeholder="Search keywords (q)"
          className="border p-2 rounded"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Autocomplete
          size="small"
          options={[{ id: "", label: "All Providers" }, ...providers]}
          getOptionLabel={(o) => o?.label || ""}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          onChange={(_e, val) =>
            setFilters((p) => ({
              ...p,
              providerUserId: val?.id || "",
              page: 1,
            }))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Company"
              placeholder="Type company name"
            />
          )}
          value={
            providers.find((p) => p.id === filters.providerUserId) || {
              id: "",
              label: "All Providers",
            }
          }
        />
        <FormControl size="small">
          <InputLabel id="admin-listings-cat-label">Categories</InputLabel>
          <MUISelect
            labelId="admin-listings-cat-label"
            multiple
            value={filters.categoryIds}
            onChange={handleCategoriesChange}
            input={<OutlinedInput label="Categories" />}
            renderValue={(selected) =>
              (selected || [])
                .map((id) => categories.find((c) => c._id === id)?.name || id)
                .join(", ")
            }
          >
            {(categories || []).map((c) => (
              <MenuItem key={c._id} value={c._id}>
                <Checkbox checked={filters.categoryIds.indexOf(c._id) > -1} />
                <ListItemText primary={c.name} />
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
        <FormControl
          size="small"
          disabled={!filters.categoryIds || filters.categoryIds.length === 0}
        >
          <InputLabel id="admin-listings-subcat-label">
            Subcategories
          </InputLabel>
          <MUISelect
            labelId="admin-listings-subcat-label"
            multiple
            value={filters.subcategoryIds}
            onChange={handleSubcategoriesChange}
            input={<OutlinedInput label="Subcategories" />}
            renderValue={(selected) =>
              (selected || [])
                .map(
                  (id) => subcategories.find((s) => s._id === id)?.name || id
                )
                .join(", ")
            }
          >
            {subcategories.map((s) => (
              <MenuItem key={s._id} value={s._id}>
                <Checkbox
                  checked={filters.subcategoryIds.indexOf(s._id) > -1}
                />
                <ListItemText primary={s.name} />
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>
        <input
          type="number"
          placeholder="Min price"
          className="border p-2 rounded"
          value={filters.priceMin}
          onChange={(e) =>
            setFilters((p) => ({ ...p, priceMin: e.target.value }))
          }
        />
        <input
          type="number"
          placeholder="Max price"
          className="border p-2 rounded"
          value={filters.priceMax}
          onChange={(e) =>
            setFilters((p) => ({ ...p, priceMax: e.target.value }))
          }
        />
        <select
          className="border p-2 rounded"
          value={filters.sort}
          onChange={(e) => setFilters((p) => ({ ...p, sort: e.target.value }))}
        >
          <option value="created_desc">Newest</option>
          <option value="price_asc">Price: Low to High</option>
          <option value="price_desc">Price: High to Low</option>
        </select>
        <div className="flex items-center gap-2">
          <button
            className="px-4 py-2 bg-gray-600 text-white rounded"
            onClick={() => {
              setSubcategories([]);
              setSearchText("");
              setFirstSearchDone(false);
              setListings([]);
              setListingsTotal(0);
              setFilters({
                q: "",
                categoryIds: [],
                subcategoryIds: [],
                tags: "",
                priceMin: "",
                priceMax: "",
                lat: "",
                lng: "",
                sort: "created_desc",
                page: 1,
                limit: filters.limit,
                providerUserId: "",
              });
              navigate({ pathname: location.pathname }, { replace: true });
              runSearch({ page: 1 }).finally(() => setFirstSearchDone(true));
            }}
          >
            Reset
          </button>
        </div>
      </div>

      {!firstSearchDone ? (
        <div className="bg-white shadow rounded p-6 text-center text-gray-500">
          Loading...
        </div>
      ) : (
        <div className="bg-white shadow rounded">
          <div className="overflow-x-auto">
            <table className="w-full min-w-max text-center">
              <thead>
                <tr>
                  <th className="p-2 border">Image</th>
                  <th className="p-2 border">Name</th>
                  <th className="p-2 border">Price</th>
                  <th className="p-2 border">Provider</th>
                  <th className="p-2 border">Category</th>
                  <th className="p-2 border">Subcategory</th>
                  <th className="p-2 border">Address</th>
                  <th className="p-2 border">Radius (km)</th>
                  <th className="p-2 border">Location</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((it) => {
                  const imageUrls = Array.isArray(it?.images)
                    ? it.images
                        .map((img) =>
                          typeof img === "string" ? img : img?.url
                        )
                        .filter(Boolean)
                    : [];
                  const firstImage =
                    imageUrls.length > 0
                      ? toApiAbsoluteUrl(imageUrls[0])
                      : null;
                  const placeholder = `https://placehold.co/80x60/png?text=${encodeURIComponent(
                    (it?.name || "No Image").slice(0, 10)
                  )}`;
                  const providerDisplay =
                    it?.provider?.providerProfile?.companyName ||
                    it?.provider?.email ||
                    "-";
                  const categoryDisplay =
                    it?.category?.name ||
                    (it &&
                    typeof it.categoryId === "object" &&
                    it.categoryId !== null
                      ? it.categoryId.name || it.categoryId._id
                      : it?.categoryId) ||
                    "-";
                  const subcategoryDisplay =
                    it?.subcategory?.name ||
                    (it &&
                    typeof it.subcategoryId === "object" &&
                    it.subcategoryId !== null
                      ? it.subcategoryId.name || it.subcategoryId._id
                      : it?.subcategoryId) ||
                    "-";
                  return (
                    <tr key={it.id}>
                      <td className="p-2 border">
                        <img
                          src={firstImage || placeholder}
                          alt={it.name}
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = placeholder;
                          }}
                          className="w-14 h-10 object-cover rounded"
                        />
                      </td>
                      <td className="p-2 border">{it.name}</td>
                      <td className="p-2 border tabular-nums font-mono tracking-tight">
                        {typeof it.price === "number"
                          ? currencyFmt.format(it.price)
                          : "-"}
                      </td>
                      <td className="p-2 border">{providerDisplay}</td>
                      <td className="p-2 border">
                        <Chip
                          label={categoryDisplay || "-"}
                          size="small"
                          variant="outlined"
                        />
                      </td>
                      <td className="p-2 border">
                        <Chip
                          label={subcategoryDisplay || "-"}
                          size="small"
                          variant="outlined"
                        />
                      </td>
                      <td className="p-2 border">{it.address || "-"}</td>
                      <td className="p-2 border">{it.maxRadiusKm ?? "-"}</td>
                      <td className="p-2 border">
                        {Array.isArray(it?.location?.coordinates)
                          ? (() => {
                              const lng = it.location.coordinates[0];
                              const lat = it.location.coordinates[1];
                              const url = `https://www.google.com/maps?q=${lat},${lng}`;
                              return (
                                <button
                                  onClick={() =>
                                    window.open(url, "_blank", "noopener")
                                  }
                                  className="inline-flex items-center text-blue-600 hover:text-blue-800"
                                  title={`Open in Maps (${lat}, ${lng})`}
                                >
                                  <MapPin size={18} />
                                </button>
                              );
                            })()
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
                {listings.length === 0 && (
                  <tr>
                    <td className="p-6 text-gray-500 text-center" colSpan={9}>
                      No listings
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between p-3 flex-wrap gap-3">
            <div className="text-sm text-gray-600">Total: {listingsTotal}</div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                disabled={filters.page <= 1}
                onClick={() =>
                  runSearch({ page: Math.max(1, (filters.page || 1) - 1) })
                }
              >
                Prev
              </button>
              <span className="text-sm">Page {filters.page}</span>
              <button
                className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                disabled={listings.length < filters.limit}
                onClick={() => runSearch({ page: (filters.page || 1) + 1 })}
              >
                Next
              </button>
              <select
                className="ml-2 border p-1 rounded text-sm"
                value={filters.limit}
                onChange={(e) =>
                  runSearch({ limit: Number(e.target.value), page: 1 })
                }
              >
                <option value={10}>10 / page</option>
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="mt-2">
          <LinearProgress />
        </div>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
